import { type Event, Property, User } from '@kalos/kalos-rpc';
import { useCallback, useRef, useState } from 'react';

import { useSessionStorage } from '../../../../hooks/useSessionStorage';
import { type DirtyModelFields } from '../../../../tools/helpers';
import { sessionStorageManager } from '../../../../tools/storageManager';
import { type JobType } from '../components/JobTypeSelectionStep';
import { type JobCreationWizardPersistedState, type JobCreationWizardStep } from '../types';

const STORAGE_KEY = 'jobCreationWizardDraft';
/** Drop a persisted draft older than this so stale work doesn't resurface in a long-lived tab. */
const DRAFT_TTL_MS = 12 * 60 * 60 * 1000; // 12 hours

type WizardSnapshot = {
  step: JobCreationWizardStep;
  customer: User | null;
  property: Property | null;
  jobType: JobType | null;
  selectedDate: string;
  customerSearch: string;
  draft: Partial<Event>;
  dirtyFields: DirtyModelFields<Record<string, unknown>>;
};

const isExpired = (snapshot: { savedAt?: unknown } | null | undefined): boolean =>
  !snapshot || typeof snapshot.savedAt !== 'number' || Date.now() - snapshot.savedAt > DRAFT_TTL_MS;

/**
 * True when the snapshot holds nothing the user entered — the state of a freshly
 * mounted wizard. jobType is deliberately not considered: mount sites may preset
 * it (allowedJobType) without any user input.
 */
const isPristineSnapshot = (
  snapshot: Pick<WizardSnapshot, 'step' | 'customer' | 'property' | 'customerSearch' | 'draft'>,
): boolean =>
  snapshot.step === 'customer' &&
  !snapshot.customer &&
  !snapshot.property &&
  !snapshot.customerSearch &&
  Object.keys(snapshot.draft ?? {}).length === 0;

/**
 * Persists the in-progress new-job wizard to sessionStorage so a remount (tab
 * switch, window-focus refetch, auth-token refresh, page refresh) doesn't reset
 * the wizard to step 1 and wipe the user's input.
 *
 * @param enabled persist only for new jobs — pass `!isEditing`. When false, the
 *   hook is inert: nothing is restored and `save`/`clear` are no-ops.
 * @returns `restored` — the snapshot to seed state from on mount (or null), with
 *   customer/property rebuilt as real message instances — plus `save`/`clear`.
 */
export const useJobCreationPersistence = (enabled: boolean) => {
  const [persisted, setPersisted, clearPersisted] = useSessionStorage(STORAGE_KEY, null, {
    initializeWithValue: enabled,
  });

  // Resolve the snapshot to restore exactly once, from the value present on
  // mount. A lazy useState initializer runs a single time, so our own later
  // saves never re-trigger a "restore" mid-session.
  const [restored] = useState<JobCreationWizardPersistedState | null>(() => {
    if (!enabled || !persisted || isExpired(persisted)) return null;

    return {
      ...persisted,
      // sessionStorage round-trips messages to plain objects; rebuild them.
      customer: persisted.customer ? User.create(persisted.customer) : null,
      property: persisted.property ? Property.create(persisted.property) : null,
    };
  });

  // All wizard mount sites share one storage key, and each instance saves its
  // snapshot on mount. Until THIS instance has saved something meaningful, its
  // pristine (empty) snapshot must not clobber a meaningful snapshot another
  // instance stored — a later remount would restore a draft missing the user's
  // input (e.g. a Commercial sector selection).
  const hasSavedMeaningfulRef = useRef(false);

  const save = useCallback(
    (snapshot: WizardSnapshot) => {
      if (!enabled) return;

      const pristine = isPristineSnapshot(snapshot);
      if (!hasSavedMeaningfulRef.current && pristine) {
        // Read storage directly: going through React state here would force the
        // stored value into this callback's deps and churn `save`'s identity on
        // every write (the save-on-mount effect depends on it).
        const stored = sessionStorageManager.get(STORAGE_KEY);
        if (stored && !isExpired(stored) && !isPristineSnapshot(stored)) return;
      }
      if (!pristine) hasSavedMeaningfulRef.current = true;

      setPersisted({ ...snapshot, savedAt: Date.now() });
    },
    [enabled, setPersisted],
  );

  const clear = useCallback(() => {
    if (!enabled) return;
    clearPersisted();
  }, [enabled, clearPersisted]);

  return { restored, save, clear };
};
